<template>
  <div class="sequence-page">
    <PostprocessingTemplateEditModal
      v-if="postprocessingToEdit"
      name="sequence-postprocessing-edit"
      :postprocessingTemplate="postprocessingToEdit"
      :isPreselectMode="false"
      :isCalibrationSequenceMode="true"
      @update:sequencePostprocessing="handleSequencePostprocessingUpdated"
    />
    <div class="top-line">
      <RouterLink id="link-navigate-back" class="link-navigate-back" to="/sequences/">
        <i class="material-icons material-icon--16 no-text-decoration">keyboard_backspace</i>
        Sequences
      </RouterLink>
    </div>

    <div v-if="sequence != null" class="paper-block-container paper-block-container--main mt-4">
      <div class="sequence-page__layout">
        <div class="pb-2 sm:ml-10">
          <div class="paper__header">
            <div>
              <InputTitle v-model="sequence.name" iconName="group_work" @save="saveName" />
              <div class="pt-1">
                <!--              <span v-if="isCalibrationMode" id="sequence-id" class="mr-3"-->
                <!--                >Calibration sequence</span-->
                <!--              >-->
                <span id="sequence-id" class="mr-3">Sequence</span>

                <span
                  class="mr-3"
                  style="color: #00000088"
                  v-html="new Date(sequence.created).toLocaleDateString()"
                />

                <div v-if="device && device.id != null" style="margin-right: 12px; display: inline">
                  <RouterLink :to="linkToDevicePage">
                    <IconMaterial title="input" :size="16" />
                    {{ device.name }}
                  </RouterLink>
                </div>

                <StatesForEntity
                  v-if="sequence.state !== '-'"
                  :state="sequence.state"
                  class="mr-3"
                />

                <StateArchived v-if="sequence.archived" class="mr-3" />
              </div>
            </div>
            <div class="sequence-page__button-group">
              <button v-if="sequence.archived" class="button--square-image" @click="restore">
                <i class="material-icons material-icon--18">restore</i>
                Restore
              </button>
              <button v-else-if="isEditMode" class="button--square-image" @click="finishEditing">
                <i class="material-icons material-icon--18">done</i>
                Finish <br />
                editing
              </button>

              <div v-show="!sequence.archived && !isEditMode" class="sequence-page__button-column">
                <Btn
                  type="primary-transparent"
                  icon-left="edit"
                  padding="xs"
                  height="s"
                  class="sequence-page__action_button"
                  @click="startEditing"
                >
                  Edit
                </Btn>
                <Btn
                  type="primary-transparent"
                  icon-left="content_copy"
                  padding="xs"
                  height="s"
                  class="sequence-page__action_button"
                  @click="clone"
                >
                  Clone
                </Btn>
                <Btn
                  v-if="sequence.can_archive"
                  type="primary-transparent"
                  icon-left="delete"
                  padding="xs"
                  height="s"
                  :disabled="sequence.state === 'EXE' || sequence.state === 'PAU'"
                  class="sequence-page__action_button"
                  @click="archive"
                >
                  Archive
                </Btn>
              </div>
            </div>
          </div>

          <div class="paper__content--margin-32">
            <ModelTextareaComponent
              ref="description"
              v-model="sequence.description"
              :canEdit="sequence.can_edit"
              :label="'Description'"
              style="margin-top: 16px"
              :isEditMode="isEditMode"
              @update:value="saveDescription"
              @click="startEditing"
            />

            <!--          <label-->
            <!--            v-if="sequence.compounds || isEditMode"-->
            <!--            class="label-block-margin"-->
            <!--            style="margin: 16px 0 0"-->
            <!--          >-->
            <!--            Compounds-->
            <!--          </label>-->
            <!--          <SelectorMultipleCompounds-->
            <!--            v-if="isEditMode"-->
            <!--            :compounds="compoundsParsed"-->
            <!--            @update:compounds="saveCompounds"-->
            <!--          />-->
            <!--          <div v-else-if="sequence.compounds" @click="startEditing">-->
            <!--            {{ sequence.compounds }}-->
            <!--          </div>-->
          </div>

          <div
            v-if="isCalibrationMode"
            class="paper__content--margin-32 sequence-page__calibration-mode"
          >
            <div class="sequence-page__calibration-mode-label">In calibration mode</div>
            <DeviceMethodColumnComponent
              :editable="false"
              :column="column"
              :device="device"
              :method="method"
              :postprocessingsToApply="postprocessingsToApply"
              :isCalibrationSequence="isCalibrationMode"
              style="margin: 4px 0 0"
              @editPostprocessing="editPostprocessing"
            />

            <PostprocessingProgressComponent v-if="isSequenceFinished" :sequenceId="sequence.id" />
          </div>
        </div>

        <div class="sequence-page__nav-mobile">
          <button
            v-for="item in navigationItems"
            :key="item.view"
            type="button"
            class="sequence-page__nav-mobile-item"
            :class="{ 'sequence-page__nav-mobile-item--active': currentView === item.view }"
            @click="navigateToView(item.view)"
          >
            <i class="material-icons">{{ item.icon }}</i>
            <span>{{ item.label }}</span>
          </button>
        </div>

        <div class="sequence-page__content-with-nav">
          <div class="sequence-page__nav-desktop">
            <button
              v-for="item in navigationItems"
              :key="item.view"
              type="button"
              class="sequence-page__nav-desktop-item"
              :class="{ 'sequence-page__nav-desktop-item--active': currentView === item.view }"
              @click="navigateToView(item.view)"
            >
              <i class="material-icons">{{ item.icon }}</i>
              <span>{{ item.label }}</span>
            </button>
          </div>
          <div class="sequence-page__content">
            <div v-if="currentView === 'calibrations' && isCalibrationMode">
              <CalibrationPanel
                :sequenceId="sequence.id"
                :defaultExpanded="true"
                :calibrationId="calibrationId"
                @showMeasurement="handleShowMeasurement"
                @showStandard="handleShowStandard"
                @changeCalibration="handleChangeCalibration"
              />
            </div>
            <div v-else-if="currentView === 'results'" class="sequence-page__results">
              <SequenceResults
                :sequenceId="sequence.id"
                @showInjection="handleShowInjection"
                @showReportGenerator="showReportGenerator"
              />
            </div>
            <div v-else-if="currentView === 'reports'" class="sequence-page__results">
              <SequenceReport :sequenceId="sequence.id" :isCalibrationMode="isCalibrationMode" />
            </div>
            <template v-else>
              <div class="mt-6">
                <div class="buttons--float-right">
                  <PopupHelper
                    v-if="sequence.can_start"
                    text="Starts sequence from next available instruction."
                    class="sequence-page__wrapper-btn"
                  >
                    <button
                      :disabled="!canStartSequence"
                      class="button--square-image-primary"
                      @click="tableStart"
                    >
                      <i class="material-icons material-icon--18">play_arrow</i>
                      Start
                    </button>
                  </PopupHelper>
                  <PopupHelper
                    v-if="sequence.can_pause"
                    text="Pause the sequence in its current Instruction. The current injection will run to completion."
                    class="sequence-page__wrapper-btn"
                  >
                    <button class="button--square-image" @click="tablePause">
                      <i class="material-icons material-icon--18">pause</i>
                      Pause
                    </button>
                  </PopupHelper>
                  <button
                    v-if="sequence.can_resume"
                    class="button--square-image-primary"
                    @click="tableResume"
                  >
                    <i class="material-icons material-icon--18">play_arrow</i>
                    Resume
                  </button>
                  <PopupHelper
                    v-if="sequence.can_stop"
                    text="Stops the sequence. The current injection will run to completion. The current instruction cannot be resumed."
                    class="sequence-page__wrapper-btn"
                  >
                    <button class="button--square-image-red" @click="tableStop">
                      <i class="material-icons material-icon--18">stop</i>
                      Stop
                    </button>
                  </PopupHelper>
                </div>

                <div class="mb-2">
                  <h4
                    style="
                      margin: 0 0 8px 32px;
                      padding-top: 24px;
                      white-space: nowrap;
                      width: initial;
                      display: inline-block;
                    "
                  >
                    Sequence Table
                  </h4>
                </div>
              </div>

              <transition-expand>
                <div v-if="activeSample" style="margin-top: 16px; width: 100%; display: block">
                  <div
                    style="
                      margin-right: 32px;
                      margin-left: 32px;
                      display: flex;
                      justify-content: space-between;
                    "
                  >
                    <div style="padding-top: 3px">
                      <div>
                        <StateProcess title="Running" class="mr-2" />
                        <IconStandard v-if="activeSample.type === 'C'" />
                        <IconSample v-else />
                        <span>
                          {{ activeSample.name }}
                        </span>
                      </div>
                      <div class="text-process mt-2">
                        stop or wait it's completion to execute sequence table
                      </div>
                    </div>
                    <PopupHelper text="View the current injection.">
                      <Btn
                        class="ml-4"
                        height="s"
                        icon-left="visibility"
                        @click="selectSample(activeSample.id, undefined, true)"
                      >
                        View
                      </Btn>
                    </PopupHelper>
                  </div>
                </div>
              </transition-expand>

              <SequenceTableComponent
                v-if="children && Object.keys(children).length > 0"
                :current="currentDraft"
                :method-default="method"
                :configurationForMethod="device.configuration_for_method"
                :samples="children"
                :table="table"
                :device="device"
                :isCalibrationMode="isCalibrationMode"
                class="mt-6"
                @update:draft="updateDraft"
                @update:table="updateTable"
                @removeDraft="removeDraft"
                @view="(draft) => selectSample(draft.sample, draft.sample_injection, true)"
                @add="draftAdd"
              />

              <!--Samples block-->
              <div ref="samples-block" class="sequence-page__sample block--shadow-in-top">
                <div v-if="sid == null">
                  <h4
                    style="margin: 40px 16px 24px 32px; display: inline-block"
                    @click="selectSample(null)"
                  >
                    Samples
                  </h4>
                  <ShowArchivedToggle v-model="isShowArchived" :n-archived="hasArchived" />
                </div>
                <button
                  v-else
                  class="rect-button sequence-page__btn-back-desktop"
                  style="margin: 0; padding: 32px 32px 8px 32px; height: 80px"
                  @click="selectSample(null)"
                >
                  <i
                    class="material-icons material-icon--16 no-text-decoration"
                    style="margin-bottom: 1px"
                  >
                    keyboard_backspace
                  </i>
                  Samples
                </button>

                <div v-show="sid == null" style="padding-bottom: 32px">
                  <table class="table-default table-default--offset-32">
                    <thead
                      v-if="childrenFiltered && childrenFiltered.length > 0"
                      style="background-color: #0000000a"
                    >
                      <tr>
                        <th class="td--shrink"></th>
                        <th class="sequence-page__cell--sortable" @click="sortBy('name')">
                          Name
                          <SortDirection
                            :direction="sortedBy.direction"
                            class="sequence-page__sort-direction"
                            :class="{
                              'sequence-page__sort-direction--visible': sortedBy.field === 'name',
                            }"
                          />
                        </th>
                        <th></th>
                        <th class="sequence-page__cell--sortable" @click="sortBy('last_modified')">
                          Date
                          <SortDirection
                            :direction="sortedBy.direction"
                            class="sequence-page__sort-direction"
                            :class="{
                              'sequence-page__sort-direction--visible':
                                sortedBy.field === 'last_modified',
                            }"
                          />
                        </th>
                        <th @click="sortBy('last_modified')"></th>
                      </tr>
                    </thead>
                    <tbody
                      v-if="childrenFiltered && childrenFiltered.length > 0"
                      class="tbody__td--nowrap"
                    >
                      <tr
                        v-for="child in childrenFiltered"
                        :key="child.id"
                        class="row--clickable"
                        @click="selectSample(child.id)"
                      >
                        <td class="td--shrink">
                          <i
                            v-if="child.archived"
                            class="material-icons material-icon--16 text-red"
                          >
                            delete
                          </i>
                          <i
                            v-else-if="child.type === 'S'"
                            class="material-icons material-icon--16"
                          >
                            opacity
                          </i>
                          <i
                            v-else-if="child.type === 'C'"
                            class="material-icons material-icon--16"
                          >
                            star
                          </i>
                        </td>
                        <td class="text-bold-blue td--overflow-hidden">
                          {{ child.name }}
                        </td>
                        <td class="text-gray">
                          <span v-if="child.amount != null"> {{ child.amount }} mg/mL </span>
                        </td>
                        <td class="td--shrink text-gray" v-html="formatDate(child.last_modified)" />
                        <td
                          class="td--shrink text-gray right"
                          :class="{
                            'sequence-page__cell--selected': child.id === Number(selectedItemId),
                          }"
                        >
                          {{ formatTime(child.last_modified) }}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr v-if="isShowSampleCreationForm">
                        <td colspan="6" style="padding-top: 1px; padding-bottom: 1px">
                          <div class="sequence-page__form-create-sample">
                            <input
                              ref="inputNewSampleName"
                              v-model="inputs.sampleName"
                              type="text"
                              required
                              placeholder="Sample name"
                              class="sequence-page__input-name"
                            />
                            <Btn
                              type="primary"
                              :isSubmit="true"
                              class="sequence-page__btn-add-sample"
                              padding="xs"
                              iconLeft="add"
                              @click="addSample"
                            >
                              Sample
                            </Btn>
                            <Btn
                              class="sequence-page__btn-hide-form"
                              padding="none"
                              @click="hideSampleCreationForm"
                            >
                              <IconMaterial title="close" />
                            </Btn>
                          </div>
                        </td>
                      </tr>
                      <tr v-else>
                        <td colspan="6" @click="showSampleCreationForm">
                          <IconMaterial
                            title="add"
                            size="16"
                            class="sequence-page__icon-add-sample"
                          />
                          <span class="sequence-page__label-add-sample"> Add sample </span>
                        </td>
                      </tr>

                      <tr v-if="isShowStandardCreationForm">
                        <td colspan="6" style="padding-top: 1px; padding-bottom: 1px">
                          <div class="sequence-page__form-create-sample">
                            <div class="flex flex-col gap-1 flex-1">
                              <div>
                                <label class="label-block-margin" style="margin: 16px 0 0">
                                  Compound
                                </label>
                                <CompoundSelector
                                  v-model="inputs.compound"
                                  :sequenceId="sequence.id"
                                />
                              </div>
                              <!--                      <CompoundAutosuggest-->
                              <!--                        ref="name"-->
                              <!--                        v-model="inputs.compound"-->
                              <!--                        placeholder="Compound name"-->
                              <!--                        :isShowAllSuggestionsOnFocus="true"-->
                              <!--                      >-->
                              <!--                        <template-->
                              <!--                          #default="{-->
                              <!--                            value,-->
                              <!--                            setValue,-->
                              <!--                            handleInputFocus,-->
                              <!--                            handleInputBlur,-->
                              <!--                            isDisabled,-->
                              <!--                            placeholder,-->
                              <!--                          }"-->
                              <!--                        >-->
                              <!--                          <ModelFieldComponent-->
                              <!--                            ref="inputNewStandardName"-->
                              <!--                            :value="value"-->
                              <!--                            :label="placeholder"-->
                              <!--                            :disabled="isDisabled"-->
                              <!--                            @update:value="setValue($event)"-->
                              <!--                            @focus="handleInputFocus"-->
                              <!--                            @blur="handleInputBlur"-->
                              <!--                          />-->
                              <!--                        </template>-->

                              <!--                        <template #mobileTrigger="{ value }">-->
                              <!--                          <input-->
                              <!--                            :value="value"-->
                              <!--                            type="text"-->
                              <!--                            disabled-->
                              <!--                            class="standard-new__input-title"-->
                              <!--                          />-->
                              <!--                        </template>-->
                              <!--                      </CompoundAutosuggest>-->
                              <div class="sequence-page__wrapper-concentration">
                                <ModelFieldComponent
                                  v-model="inputs.amount"
                                  type="float"
                                  required
                                  label="Standard concentration"
                                  class="sequence-page__input-standard"
                                />
                                <select
                                  v-model="inputs.unit"
                                  required
                                  :disabled="isCompoundUsed"
                                  class="sequence-page__input-unit"
                                >
                                  <option value="%">%</option>
                                  <option value="mg/mL">mg/mL</option>
                                  <option value="mol">mol</option>
                                  <option value="ppm">ppm</option>
                                </select>
                              </div>
                              <ModelFieldComponent
                                v-model="inputs.peak_detection_start"
                                type="float"
                                required
                                :disabled="isPeakDetectionLocked"
                                label="Start of peak search time, min"
                                class="sequence-page__input-standard"
                              />
                              <ModelFieldComponent
                                v-model="inputs.peak_detection_end"
                                type="float"
                                required
                                :disabled="isPeakDetectionLocked"
                                label="End of peak search time, min"
                                class="sequence-page__input-standard"
                              />
                              <div
                                v-if="peakDetectionLockReason"
                                class="sequence-page__warning-not-editable"
                              >
                                {{ peakDetectionLockReason }}
                              </div>
                            </div>

                            <Btn
                              v-if="isCalibrationMode"
                              type="primary"
                              :isSubmit="true"
                              class="sequence-page__btn-add-sample"
                              padding="xs"
                              iconLeft="add"
                              @click="addStandard"
                            >
                              Standard
                            </Btn>
                            <Btn
                              class="sequence-page__btn-hide-form"
                              padding="none"
                              @click="hideStandardCreationForm"
                            >
                              <IconMaterial title="close" />
                            </Btn>
                          </div>
                        </td>
                      </tr>
                      <tr v-else-if="isCalibrationMode">
                        <td colspan="6" @click="showStandardCreationForm">
                          <IconMaterial
                            title="add"
                            size="16"
                            class="sequence-page__icon-add-sample"
                          />
                          <span class="sequence-page__label-add-sample"> Add standard </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div v-show="sid != null" class="sequence__samples-expanded">
                  <div class="sequence-page__wrapper-pager">
                    <button
                      class="rect-button sequence-page__btn-back-adaptive"
                      @click="selectSample(null)"
                    >
                      <i
                        class="material-icons material-icon--16 no-text-decoration"
                        style="margin-bottom: 1px"
                      >
                        keyboard_backspace
                      </i>
                      Samples
                    </button>
                    <PagerComponent
                      v-if="sid != null"
                      :current="selectedSampleIndex + 1"
                      :next-enabled="nextID"
                      :prev-enabled="prevID"
                      :total="totalSamples"
                      class="samples__pager"
                      @next="next"
                      @prev="prev"
                      @prevToStart="goToFirstSample"
                      @nextToEnd="goToLastSample"
                    />
                  </div>
                  <div class="samples__sample-tabs-container">
                    <div class="samples__sample-tabs-inner-container">
                      <div
                        v-for="child in childrenFiltered"
                        :key="child.id"
                        :ref="'tab' + child.id.toString()"
                        :class="{
                          'samples__sample-tab-button--selected': sid === child.id,
                          'samples__sample-tab-button--gray': child.archived,
                        }"
                        class="samples__sample-tab-button"
                        @click="selectSample(child.id)"
                      >
                        {{ child.name }}
                      </div>
                    </div>
                  </div>
                  <div class="samples__sample-expanded-container">
                    <SampleComponent
                      v-if="safeSampleID != null"
                      v-show="sid != null && children[sid].type === 'S'"
                      :id="safeSampleID"
                      ref="selectedSample"
                      :injectionID="iid"
                      :sampleData="children[safeSampleID]"
                      :isPaused="isSamplePaused(safeSampleID)"
                      :isVisible="sid != null && children[sid].type === 'S'"
                      @update:injectionID="newInjectionID"
                      @archive="hideCurrentChild"
                    />
                    <StandardComponent
                      v-if="safeStandardID"
                      v-show="sid != null && children[sid].type === 'C'"
                      :id="safeStandardID"
                      ref="selectedSample"
                      :standardData="children[safeStandardID]"
                      :isPaused="isSamplePaused(safeStandardID)"
                      :injectionID="iid"
                      @update:injectionID="newInjectionID"
                      @edit="refreshChildren"
                      @archive="hideCurrentChild"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <ErrorComponent v-else-if="errorCode != null" :code="errorCode" style="margin-top: 64px" />
    <loading-component v-else />
  </div>
</template>

<script>
  import 'assets/css/base/table.scss';

  import _ from 'lodash';

  import StateArchived from '@/uikitProject/states/StateArchived';
  import StandardComponent from 'components/block/StandardComponent';
  import ErrorComponent from 'components/element/ErrorComponent';
  import LoadingComponent from 'components/element/LoadingComponent';
  import ModelTextareaComponent from 'components/element/ModelTextareaComponent';
  import PageTitle from '@/mixins/page-title';
  import SampleComponent from 'components/block/SampleComponent';
  import SequenceSocket, { SequenceSocketEvents } from 'api/sockets/SequenceSocket';
  import SequenceTableComponent from '@/components/block/vueSequenceTable/SequenceTable.vue';
  import ShowArchivedToggle from 'components/element/ShowArchivedToggle';
  import StatesForEntity from '@/uikitProject/states/packs/StatesForEntity';
  import PagerComponent from 'components/block/PagerComponent';
  import TransitionExpand from '@/uikitProject/transitions/TransitionExpand';
  import Btn from '@/uikitBase/btns/Btn';
  import IconSample from '@/uikitProject/icons/IconSample';
  import IconStandard from '@/uikitProject/icons/IconStandard';
  import StateProcess from '@/uikitProject/states/StateProcess';
  import InputTitle from '@/uikitProject/inputs/inputTitle/InputTitle';
  import IconMaterial from '@/uikitBase/icons/IconMaterial';
  import SortDirection from '@/uikitProject/tables/SortDirection';
  import SortHelper from 'utils/SortHelper.ts';
  import { consoleHelpers } from 'utils/logHelpers';
  import PopupHelper from '@/uikitProject/popups/info/PopupHelper.vue';
  import DeviceMethodColumnComponent from '@/components/element/DeviceMethodColumnComponent.vue';
  import ModelFieldComponent from '@/components/element/ModelFieldComponent.vue';
  import CompoundSelector from '@/uikitProject/compounds/CompoundSelector.vue';
  import { parsePostprocessingTemplate } from '@/utils/postprocessingTemplateHelpers';
  import PostprocessingTemplateEditModal from '@/components/block/modal/PostprocessingTemplateEditModal.vue';
  import PostprocessingProgressComponent from '@/components/element/PostprocessingProgressComponent.vue';
  import CalibrationPanel from '@/components/blocks/reactComponents/CalibrationPanel.vue';
  import SequenceReport from '@/components/blocks/reactComponents/SequenceReport.vue';
  import SequenceResults from '@/components/blocks/reactComponents/SequenceResults.vue';
  import { apiSequences } from '@/api/graphql/cloud/sequences';
  import { apiMeasurements } from '@/api/graphql/cloud/measurements';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import { DEFAULT_VIAL } from '@/constants/hardware/default';
  import { calculateRemainingCapacity } from '@/utils/sequences/scheduledInjectionLimitsHelpers';

  export default {
    name: 'SequencePage',

    components: {
      PostprocessingTemplateEditModal,
      PostprocessingProgressComponent,
      CalibrationPanel,
      SequenceReport,
      SequenceResults,
      CompoundSelector,
      ModelFieldComponent,
      DeviceMethodColumnComponent,
      PopupHelper,
      SortDirection,
      IconMaterial,
      StateProcess,
      IconStandard,
      IconSample,
      Btn,
      TransitionExpand,
      InputTitle,
      PagerComponent,
      ErrorComponent,
      ShowArchivedToggle,
      StandardComponent,
      LoadingComponent,
      SequenceTableComponent,
      ModelTextareaComponent,
      SampleComponent,
      StatesForEntity,
      StateArchived,
    },

    mixins: [PageTitle],

    props: {
      id: Number,
      sid: Number, // sample ID
      iid: Number, // injection ID
      calibrationId: Number,
    },

    data() {
      return {
        pageTitle: 'HPLC Cloud: Sequence',
        sequence: null,
        device: null,
        method: null,
        column: null,
        nameField: {
          visible: false,
          value: '',
        },
        table: null,
        children: [],
        sequenceSocket: this.createSocket(this.id),
        safeSampleID: undefined,
        safeStandardID: undefined,
        isShowArchived: false,
        errorCode: null,
        activeSample: null, // When sequence is not executing

        sortedBy: {
          field: null,
          direction: 'asc',
        },

        isShowSampleCreationForm: false,
        isShowStandardCreationForm: false,
        inputs: {
          sampleName: '',
          compound: null,
          amount: '',
          unit: 'mg/mL',
          peak_detection_start: '',
          peak_detection_end: '',
        },

        isPeakDetectionPreset: false,

        listenersGroupId: null,

        isEditMode: false,

        postprocessingToEdit: null,
      };
    },

    computed: {
      currentView() {
        const routeName = this.$route?.name;
        if (routeName === 'sequence calibrations') return 'calibrations';
        if (routeName === 'sequence results') return 'results';
        if (routeName === 'sequence reports') return 'reports';
        return 'default';
      },
      navigationItems() {
        const items = [{ view: 'default', label: 'Sequence', icon: 'view_list' }];

        if (this.isCalibrationMode) {
          items.push({ view: 'calibrations', label: 'Calibrations', icon: 'tune' });
        }

        items.push(
          { view: 'results', label: 'Results', icon: 'assessment' },
          { view: 'reports', label: 'Reports', icon: 'picture_as_pdf' },
        );

        return items;
      },
      hasArchived() {
        if (!this.children) return false;

        return _.countBy(this.children, 'archived').true || 0;
      },
      childrenFiltered() {
        if (this.isShowArchived) {
          return Object.values(this.children);
        }

        const childrenWithoutArchived = Object.values(_.pickBy(this.children, (o) => !o.archived));

        if (!this.sortedBy.field) {
          return childrenWithoutArchived;
        }

        const comparator = SortHelper.getBaseComparator(
          this.sortedBy.field === 'name' ? 'string' : 'datetime',
          this.sortedBy.direction,
        );

        childrenWithoutArchived.sort((a, b) => {
          return comparator(b[this.sortedBy.field], a[this.sortedBy.field]);
        });

        return childrenWithoutArchived;
      },

      prevID() {
        return this.childrenFiltered?.[this.selectedSampleIndex - 1]?.id;
      },
      nextID() {
        return this.childrenFiltered?.[this.selectedSampleIndex + 1]?.id;
      },
      selectedSampleIndex() {
        return this.childrenFiltered?.findIndex((child) => Number(child.id) === Number(this.sid));
      },
      totalSamples() {
        return this.childrenFiltered?.length;
      },

      isTableEmpty() {
        return this.table == null || Object.keys(this.table).length === 0;
      },

      linkToDevicePage() {
        return {
          name: 'device',
          params: { id: this.device?.id },
        };
      },

      selectedItemId() {
        return this.sid != null ? Number(this.sid) : undefined;
      },

      // compoundsParsed() {
      //   return (
      //     this.sequence?.compounds?.split(';').map((compound) => ({ name: compound.trim() })) ?? []
      //   );
      // },

      enabledDrafts() {
        return Object.values(this.table).filter((draft) => draft.enabled);
      },
      areAllEnabledDraftsValid() {
        return this.enabledDrafts.every((draft) => this.isValidDraft(draft));
      },
      canStartSequence() {
        return (
          !this.isTableEmpty &&
          this.enabledDrafts.length > 0 &&
          !this.activeSample &&
          this.areAllEnabledDraftsValid
        );
      },

      currentDraft() {
        const { state } = this.sequence;
        return state === 'EXE' || state === 'PAU' || state === '-'
          ? {
              ...this.sequence.draft_info,
              sampleId: this.table[this.sequence.draft_info.id]?.sample,
              state,
            }
          : null;
      },
      currentSample() {
        return this.currentDraft ? this.children[this.currentDraft.sampleId] : null;
      },

      isCalibrationMode() {
        return this.sequence?.is_calibration_mode ?? false;
      },

      postprocessingsToApply() {
        return Object.entries({
          [`${this.method.detector_single_channel}`]: this.sequence.postprocessing,
        });
      },

      isCompoundUsed() {
        return this.inputs.compound?.sequence_compounds?.length > 0;
      },

      isPeakDetectionLocked() {
        return this.isCompoundUsed || this.isPeakDetectionPreset;
      },

      peakDetectionLockReason() {
        if (this.isCompoundUsed) {
          return (
            'You can not edit the peak search time. The time must be the same for all uses of the ' +
            'same compound in a sequence in calibration mode. You can change the time from the ' +
            'calibration view if no injections have started yet.'
          );
        }

        if (this.isPeakDetectionPreset) {
          return 'Peak search time is defined by the calibration settings for this compound.';
        }

        return null;
      },

      isCromite() {
        return this.device.model === 'cmt';
      },

      isSequenceFinished() {
        return (
          this.sequence &&
          (this.sequence.state === 'PAU' ||
            this.sequence.state === 'REA' ||
            this.sequence.state === 'FAI')
        );
      },
    },

    watch: {
      id(id) {
        if (id == null) throw Error('Sequence id is null!');
        this.sequence = null;
        this.errorCode = null;
        this.sequenceSocket = this.createSocket(id);
      },
      sid(sid) {
        if (sid != null && this.children[sid]) {
          if (this.children[sid]?.type === 'S') {
            this.safeSampleID = sid;
          } else if (this.children[sid]?.type === 'C') {
            this.safeStandardID = sid;
          }
        }
      },
      children() {
        if (this.sid && (!this.safeSampleID || !this.safeStandardID)) {
          const { sid } = this;
          if (this.children[sid]?.type === 'S') {
            this.safeSampleID = sid;

            if (!this.isShowArchived && this.children[sid].archived) {
              this.showArchived();
            }
          } else if (this.children[sid]?.type === 'C') {
            this.safeStandardID = sid;

            if (!this.isShowArchived && this.children[sid].archived) {
              this.showArchived();
            }
          }
          this.$nextTick(() => this.scrollToTab(this.sid));
        }

        this.updateRunningInjectionNotify();
      },
      sequence(value) {
        if (value) {
          this.updateRunningInjectionNotify();
        }
      },
      'inputs.compound'(compound) {
        if (!compound) {
          this.isPeakDetectionPreset = false;
          this.inputs.peak_detection_start = '';
          this.inputs.peak_detection_end = '';
          return;
        }

        const calibrations = compound.calibrations ?? [];
        const calibrationWithDetection = calibrations.find((calibration) => {
          const detection = calibration?.calibration_compound;
          return detection?.peak_detection_start != null && detection?.peak_detection_end != null;
        });

        if (calibrationWithDetection?.calibration_compound) {
          const {
            peak_detection_start: detectionStart,
            peak_detection_end: detectionEnd,
          } = calibrationWithDetection.calibration_compound;

          this.inputs.peak_detection_start = detectionStart / 60;
          this.inputs.peak_detection_end = detectionEnd / 60;
          this.isPeakDetectionPreset = true;
        } else {
          this.isPeakDetectionPreset = false;

          if (!this.isCompoundUsed) {
            this.inputs.peak_detection_start = '';
            this.inputs.peak_detection_end = '';
          }
        }

        const calibrationWithUnit = compound.calibrations?.find((calibration) => calibration?.unit);
        const unit = compound.unit ?? calibrationWithUnit?.unit;
        if (unit) {
          this.inputs.unit = unit;
        } else {
          // Only show error if compound has calibrations but no unit
          // For new compounds without calibrations, unit will be specified later when creating standard
          const hasCalibrations = compound.calibrations && compound.calibrations.length > 0;
          if (hasCalibrations) {
            this.notifyError(
              `Unit not found for compound "${compound.name}". Please ensure the calibration level has a unit specified.`,
            );
          }
        }
      },
    },

    beforeDestroy() {
      this.destroySocket();
    },

    methods: {
      navigateToView(view) {
        if (view === this.currentView) {
          return;
        }

        const query = { ...this.$route.query };

        if (view === 'default') {
          this.$router.push({
            name: 'sequence',
            params: { id: this.id },
            query,
          });
          return;
        }

        if (view === 'calibrations') {
          const params = { id: this.id };
          if (this.calibrationId != null) {
            params.calibrationId = this.calibrationId;
          }
          this.$router.push({
            name: 'sequence calibrations',
            params,
            query,
          });
          return;
        }

        if (view === 'results') {
          this.$router.push({
            name: 'sequence results',
            params: { id: this.id },
            query,
          });
          return;
        }

        if (view === 'reports') {
          this.$router.push({
            name: 'sequence reports',
            params: { id: this.id },
            query,
          });
        }
      },
      sortBy(field) {
        this.sortedBy = {
          field,
          direction: this.sortedBy.direction === 'asc' ? 'desc' : 'asc',
        };
      },

      destroySocket() {
        if (this.sequenceSocket) {
          this.sequenceSocket.close(this.listenersGroupId);
        }
      },
      createSocket(id) {
        this.destroySocket();

        const onSequence = (s) => {
          this.sequence = s;
          this.pageTitle = `${s?.name}: Sequence HPLC.cloud`;
        };
        const onChildren = (c) => {
          this.children = c;
        };
        const onDevice = (d) => {
          this.device = d;
        };
        const onMethod = (m) => {
          this.method = m;
        };
        const onColumn = (c) => {
          this.column = c;
        };
        const onTable = (t) => {
          this.table = t; // Sequence table

          // To hide the suggestion to replace a sample
          this.$modal.hide('dialog');
        };

        const sequenceSocket = SequenceSocket.start(
          id,
          async (connection) => {
            const data = await connection.getFull();
            onSequence(data.sequence);

            if (_.has(data, 'device')) {
              onDevice(data.device);
            }
            if (_.has(data, 'method')) {
              onMethod(data.method);
            }
            if (_.has(data, 'column')) {
              onColumn(data.column);
            }
            if (_.has(data, 'children')) {
              onChildren(data.children);
            }
            if (_.has(data, 'table')) {
              onTable(data.table);
            }
          },
          (e) => {
            this.errorCode = e.code;
          },
        );

        const listenersGroup = sequenceSocket.createEventListenersGroup();
        this.listenersGroupId = listenersGroup.id;

        listenersGroup.addEventListener(SequenceSocketEvents.SEQUENCE, onSequence);
        listenersGroup.addEventListener(SequenceSocketEvents.CHILDREN, onChildren);
        listenersGroup.addEventListener(SequenceSocketEvents.DEVICE, onDevice);
        listenersGroup.addEventListener(SequenceSocketEvents.METHOD, onMethod);
        listenersGroup.addEventListener(SequenceSocketEvents.COLUMN, onColumn);
        listenersGroup.addEventListener(SequenceSocketEvents.SEQUENCE_RUN, (c, r) => {
          const children = _.clone(this.children);
          children[c.id] = c;
          this.children = children;

          // Only auto-switch if user is on the default sequence table view
          if (this.currentView === 'default') {
            this.selectSample(c.id, r.id, true);
          }

          // To hide the suggestion to replace a sample
          this.$modal.hide('dialog');
        });
        listenersGroup.addEventListener(SequenceSocketEvents.SEQUENCE_CHILD, (c) =>
          this.setChild(c),
        );
        listenersGroup.addEventListener(SequenceSocketEvents.SEQUENCE_SELECT, (cID, rID) => {
          // Only auto-switch if user is on the default sequence table view
          if (this.currentView === 'default') {
            this.selectSample(cID, rID, true);
          }
        });
        listenersGroup.addEventListener(SequenceSocketEvents.TABLE, onTable);
        listenersGroup.addEventListener(SequenceSocketEvents.SEQUENCE_DRAFT, (d) => {
          this.table = {
            ...this.table,
            [d.id]: d,
          };
        });
        listenersGroup.addEventListener(SequenceSocketEvents.SEQUENCE_PROGRESS, (p, ps, d) => {
          if (this.sequence) {
            this.sequence.progress = p;
            this.sequence.progress_str = ps;
            this.sequence.draft_info = d;
          }
        });
        listenersGroup.addEventListener(SequenceSocketEvents.SEQUENCE_ERROR, (msg) =>
          this.notifyError(msg),
        );
        listenersGroup.addEventListener(SequenceSocketEvents.REPLACE_SAMPLE, (sample_id) => {
          const sample = this.children[sample_id];

          this.$modal.show('dialog', {
            title: 'The sample analysis is completed!',
            text: `Please, insert sample: <br> <b>${sample.name}</b>${
              sample.amount != null
                ? `, <span style="color: #7c7c7c; white-space: nowrap">${sample.amount} ${
                    sample.unit ?? ''
                  }<span/>`
                : ''
            }`,
            buttons: [
              {
                title: 'Start next',
                handler: () => {
                  this.tableResume();
                  this.$modal.hide('dialog');
                },
                class: 'vue-dialog-button red-text',
              },
              {
                title: 'Later',
                class: 'vue-dialog-button blue-text',
              },
            ],
          });
        });

        return sequenceSocket;
      },

      async scrollToTab(sid) {
        await this.$nextTick();

        // Scroll only inside samples container
        // TODO unstable
        const element = this.$refs[`tab${sid.toString()}`]?.[0];
        if (element?.parentNode?.parentNode) {
          element.parentNode.parentNode.scrollTop = element.offsetTop;
        }
      },

      async scrollToInjection() {
        // Scroll whole page till the sample
        await this.$nextTick();

        if (this.$refs['samples-block']) {
          this.$refs['samples-block'].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          });
        }
      },

      setChild(c) {
        const children = _.clone(this.children);
        children[c.id] = c;
        this.children = children;
      },

      selectSample(sid, rid, scroll = false) {
        // sid === sampleId
        // rid === injectionId ?

        let aRid = _.clone(rid);

        if (sid == null) {
          this.$router.push({
            name: 'sequence',
            params: { id: this.id },
          });
        } else {
          if (!this.children[sid]) return;

          if (scroll) {
            this.scrollToTab(sid);
            this.scrollToInjection();
          }

          if (aRid == null) {
            aRid = this.children[sid].injection_last;
          }

          if (aRid == null) {
            const locationForSingle = {
              name: 'sequence child',
              params: {
                id: this.id,
                sid,
              },
            };

            consoleHelpers.warn('SHOW SEQUENCE CHILD');
            this.$router.push(locationForSingle);
          } else {
            const locationForSingle = {
              name: 'sequence child injection',
              params: {
                id: this.id,
                sid,
                iid: aRid,
              },
            };

            consoleHelpers.warn('SHOW SEQUENCE CHILD');
            this.$router.push(locationForSingle);
          }
        }
      },

      async handleShowMeasurement(measurementId) {
        try {
          const {
            injectionId,
            sampleId,
            sequenceId,
          } = await apiInjections.getInjectionByMeasurementId(measurementId);

          const targetSequenceId = Number(sequenceId);

          if (targetSequenceId !== this.id) {
            this.$router.push({
              name: 'sequence child injection',
              params: {
                id: targetSequenceId,
                sid: sampleId,
                iid: injectionId,
              },
            });

            return;
          }

          this.selectSample(sampleId, injectionId, true);
        } catch (error) {
          console.error('Failed to open measurement injection', error);
          this.notifyError('Unable to open the injection for this measurement.');
        }
      },

      handleShowInjection({ sampleId, injectionId }) {
        if (sampleId == null || injectionId == null) {
          console.warn('Unable to open injection because sampleId or injectionId is missing.', {
            sampleId,
            injectionId,
          });
          return;
        }

        this.selectSample(sampleId, injectionId, true);
      },

      showReportGenerator() {
        this.navigateToView('reports');
      },

      handleChangeCalibration(calibrationId) {
        if (this.$route?.name !== 'sequence calibrations') {
          return;
        }

        const currentRouteCalibrationId = this.$route?.params?.calibrationId;
        if (calibrationId == null) {
          if (currentRouteCalibrationId == null) {
            return;
          }
          const navigation = {
            name: 'sequence calibrations',
            params: { id: this.id },
          };
          if (this.$router?.history?.replace) {
            this.$router.history.replace(navigation);
            return;
          }
          this.$router.replace(navigation);
          return;
        }

        if (
          currentRouteCalibrationId != null &&
          Number(currentRouteCalibrationId) === Number(calibrationId)
        ) {
          return;
        }

        const navigation = {
          name: 'sequence calibrations',
          params: { id: this.id, calibrationId },
        };
        const shouldReplace = currentRouteCalibrationId == null;
        if (shouldReplace) {
          if (this.$router?.history?.replace) {
            this.$router.history.replace(navigation);
            return;
          }
          this.$router.replace(navigation);
          return;
        }
        this.$router.push(navigation);
      },

      handleShowStandard(standardId) {
        this.selectSample(standardId, null, true);
      },

      newInjectionID(iid) {
        const { sid } = this;

        if (sid == null) {
          this.$router.push({
            name: 'sequence',
            params: { id: this.id },
          });
        } else if (iid == null) {
          this.$router.push({
            name: 'sequence child',
            params: {
              id: this.id,
              sid,
            },
          });
        } else if (iid === -1) {
          this.$router.push({
            name: 'sequence child injection new',
            params: {
              id: this.id,
              sid,
            },
          });
        } else {
          const locationForSingle = {
            name: 'sequence child injection',
            params: {
              id: this.id,
              sid,
              iid,
            },
          };

          this.$router.push(locationForSingle);
        }
      },

      saveName(name) {
        this.showNotificationIfRpcError(() => this.sequenceSocket.update({ name }));
      },
      saveDescription() {
        this.showNotificationIfRpcError(() =>
          this.sequenceSocket.update({ description: this.sequence.description }),
        );
      },
      // saveCompounds(_compounds) {
      //   const compounds = _compounds.map((compound) => compound.name).join('; ');
      //   this.sequence.compounds = compounds;
      //   this.showNotificationIfRpcError(() => this.sequenceSocket.update({ compounds }));
      // },

      updateDMC({ device, method, column }) {
        const isDeviceChanged = device.id !== this.device.id;
        const isMethodChanged = method.id !== this.method.id;
        const isColumnChanged = column.id !== this.column.id;

        if (isDeviceChanged || isMethodChanged || isColumnChanged) {
          this.showNotificationIfRpcError(() =>
            this.sequenceSocket.update({
              device_id: device.id,
              method_id: method.id,
              column_id: column.id,
            }),
          );
        }
      },

      finish() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.finish());
      },
      reject() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.reject());
      },

      archive() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.archive());
      },
      restore() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.restore());
      },

      clone() {
        return this.showNotificationIfRpcError(async () => {
          await this.doClone();
        });
      },

      async doClone() {
        const data = await this.sequenceSocket.clone();
        this.$router.push({ name: 'sequence', params: { id: data.sequence_id } });
        this.notify('Sequence was cloned!');
      },

      run() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.run());
      },
      stop() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.stop());
      },

      load() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.load());
      },

      addSample() {
        if (this.inputs.sampleName) {
          this.showNotificationIfRpcError(() =>
            this.sequenceSocket.addSample(this.inputs.sampleName),
          );

          this.inputs.sampleName = '';
          this.hideSampleCreationForm();
        }
      },
      validateStandardInputs() {
        const {
          compound,
          amount: rawAmount,
          unit,
          peak_detection_start: rawStart,
          peak_detection_end: rawEnd,
        } = this.inputs;

        const missingFields = [];
        const amount = Number(rawAmount);
        const peakDetectionStart = Number(rawStart);
        const peakDetectionEnd = Number(rawEnd);

        if (!compound) missingFields.push('Compound');
        if (rawAmount === '' || Number.isNaN(amount)) missingFields.push('Standard concentration');
        if (!unit) missingFields.push('Unit');
        if (rawStart === '' || Number.isNaN(peakDetectionStart))
          missingFields.push('Start of peak search time');
        if (rawEnd === '' || Number.isNaN(peakDetectionEnd))
          missingFields.push('End of peak search time');

        if (missingFields.length > 0) {
          this.notifyError(`All fields are required: ${missingFields.join(', ')}`);
          return null;
        }

        if (amount <= 0) {
          this.notifyError('Standard concentration must be greater than 0');
          return null;
        }

        if (peakDetectionStart >= peakDetectionEnd) {
          this.notifyError('Start time should be lower than end time!');
          return null;
        }

        return { amount, peakDetectionStart, peakDetectionEnd };
      },
      async addStandard() {
        const validated = this.validateStandardInputs();
        if (!validated) return;

        const { amount, peakDetectionStart, peakDetectionEnd } = validated;

        try {
          await this.showNotificationIfRpcError(() =>
            this.sequenceSocket.addStandard({
              name: this.inputs.compound.name,
              compound_id: this.inputs.compound.id,
              amount,
              unit: this.inputs.unit,
              peak_detection_start: peakDetectionStart * 60,
              peak_detection_end: peakDetectionEnd * 60,
            }),
          );

          this.inputs.compound = null;
          this.inputs.amount = '';
          this.inputs.unit = 'mg/mL';
          this.inputs.peak_detection_start = '';
          this.inputs.peak_detection_end = '';
          this.hideStandardCreationForm();
        } catch (error) {
          console.error('Failed to add standard:', error);
        }
      },

      draftAdd() {
        if (this.table == null) {
          this.table = {};
        }

        const table = Object.values(this.table).sort((a, b) => a.order - b.order);
        const lastNotArchivedDraft = _.findLast(
          table,
          (draft) => this.children[draft.sample] && !this.children[draft.sample].archived,
        );

        if (table.length >= 1 && lastNotArchivedDraft) {
          const desiredRepeats = Math.max(0, Math.floor(Number(lastNotArchivedDraft.repeat) || 0));
          const capacity = calculateRemainingCapacity(table, lastNotArchivedDraft.sample);
          const repeat = Math.max(0, Math.min(desiredRepeats, capacity.maxAllowed));
          const isSampleType = lastNotArchivedDraft.type !== 'C';

          table.push({
            name: lastNotArchivedDraft.name,
            type: lastNotArchivedDraft.type,
            sample: lastNotArchivedDraft.sample,
            vial: this.isCromite
              ? DEFAULT_VIAL
              : this.isCalibrationMode
              ? null
              : lastNotArchivedDraft.vial,
            amount: lastNotArchivedDraft.amount,
            unit: lastNotArchivedDraft.unit,
            method: this.isCalibrationMode
              ? lastNotArchivedDraft.method
              : this.device.method_id ?? null,
            injection_ul:
              this.isCalibrationMode && isSampleType
                ? lastNotArchivedDraft.injection_ul ??
                  (this.method?.injection_ul != null ? Math.round(this.method.injection_ul) : null)
                : null,
            repeat,
            enabled: !this.isCalibrationMode,
          });

          this.showNotificationIfRpcError(() => this.sequenceSocket.sequenceTableUpdate(table));
          return;
        }

        const firstSampleOrStandard = Object.values(this.children).find(
          (sample) => !sample.archived,
        );

        if (!firstSampleOrStandard) {
          this.notifyError('You should create at least one sample or standard');
          return;
        }

        const sampleIdForCapacity =
          firstSampleOrStandard?.sample ?? firstSampleOrStandard?.id ?? null;
        const capacity = calculateRemainingCapacity(table, sampleIdForCapacity);
        const repeat = capacity.maxAllowed > 0 ? Math.min(1, capacity.maxAllowed) : 0;
        const isSampleType = (firstSampleOrStandard?.type ?? 'S') !== 'C';

        this.table.newDraft = {
          name: firstSampleOrStandard?.name ?? null,
          type: firstSampleOrStandard?.type ?? 'S',
          sample: firstSampleOrStandard?.id ?? null,
          vial: DEFAULT_VIAL,
          method: this.isCalibrationMode ? this.sequence.method_id : this.device.method_id ?? null,
          injection_ul:
            this.isCalibrationMode && isSampleType
              ? lastNotArchivedDraft?.injection_ul ??
                (this.method?.injection_ul != null ? Math.round(this.method.injection_ul) : null)
              : null,
          repeat,
          enabled: true,
        };

        const updatedTable = Object.values(this.table).sort((a, b) => a.order - b.order);
        this.showNotificationIfRpcError(() =>
          this.sequenceSocket.sequenceTableUpdate(updatedTable),
        );
      },

      hideCurrentChild() {
        if (!this.isShowArchived) {
          this.selectSample(null);
        }
      },

      showArchived() {
        this.isShowArchived = true;
      },

      tableStart() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.sequenceTableStart());
      },
      tablePause() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.sequenceTablePause());
      },
      tableResume() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.sequenceTableResume());
      },
      tableStop() {
        this.showNotificationIfRpcError(() => this.sequenceSocket.sequenceTableStop());
      },

      prev() {
        this.selectSample(this.prevID);
      },
      next() {
        this.selectSample(this.nextID);
      },
      goToFirstSample() {
        this.selectSample(this.childrenFiltered[0].id);
      },
      goToLastSample() {
        this.selectSample(this.childrenFiltered[this.childrenFiltered.length - 1].id);
      },

      updateRunningInjectionNotify() {
        if (['EXE'].includes(this.sequence?.state)) {
          this.activeSample = null;
          return;
        }
        const runs = Object.values(this.children).filter((c) => ['RUN'].includes(c.state));
        if (runs.length > 0) {
          this.activeSample = runs[0];
        } else {
          this.activeSample = null;
        }
      },

      async showSampleCreationForm() {
        this.isShowSampleCreationForm = true;

        await this.$nextTick();
        this.$refs.inputNewSampleName?.focus();
      },
      hideSampleCreationForm() {
        this.isShowSampleCreationForm = false;
      },

      async showStandardCreationForm() {
        this.isShowStandardCreationForm = true;

        await this.$nextTick();
        this.$refs.inputNewStandardName?.focus();
      },
      hideStandardCreationForm() {
        this.isShowStandardCreationForm = false;
        this.isPeakDetectionPreset = false;
      },

      updateTable(drafts) {
        /**
         * We suppose that sometimes drafts from a previous run can be sent to the backend
         * (due to the vue rendering process)
         * In this case the backend deletes correct drafts. This check to avoid it
         */
        if (drafts.some((draft) => draft.sequence === this.sequence.id)) {
          this.showNotificationIfRpcError(() => this.sequenceSocket.sequenceTableUpdate(drafts));
        }
      },

      removeDraft(draft) {
        const drafts = Object.values(this.table).filter((_draft) => _draft.id !== draft.id);
        this.showNotificationIfRpcError(() => this.sequenceSocket.sequenceTableUpdate(drafts));
      },

      async updateDraft(draft) {
        try {
          await this.showNotificationIfRpcError(() =>
            this.sequenceSocket.sequenceTableUpdateDraft(draft),
          );
        } catch (e) {
          if (e.message === 'sequence_table_controller.draft_cannot_be_enabled') {
            this.table = {
              ...this.table,
              [draft.id]: {
                ...draft,
                enabled: false,
              },
            };
          }
        }
      },

      isSamplePaused(sampleId) {
        return this.currentDraft?.sampleId === sampleId ? this.currentDraft.state === 'PAU' : false;
      },

      startEditing() {
        if (this.sequence.can_edit) {
          this.isEditMode = true;
        }
      },
      finishEditing() {
        this.$refs.description.save();
        this.isEditMode = false;
      },

      isValidDraft(draft) {
        return draft.method && draft.repeat && draft.vial;
      },

      async refreshChildren() {
        const { children } = await this.sequenceSocket.getFull();
        this.children = children;
      },

      async editPostprocessing() {
        const {
          params_pdet,
          params_peak,
          params_base,
          params_binj,
          params_nois,
          params_dtim,
          ...postprocessingTemplate
        } = this.postprocessingsToApply[0][1];

        delete postprocessingTemplate.created_at;
        delete postprocessingTemplate.updated_at;
        delete postprocessingTemplate.user;
        delete postprocessingTemplate.organization;

        this.postprocessingToEdit = parsePostprocessingTemplate({
          ...postprocessingTemplate,
          ...params_pdet,
          ...params_peak,
          ...params_base,
          ...params_binj,
          ...params_nois,
          ...params_dtim,
        });

        await this.$nextTick();
        this.$modal.show(`sequence-postprocessing-edit`);
      },
      async restartAnalysis() {
        const { measurements } = await apiMeasurements.getMeasurementsBySequenceId(
          this.sequence.id,
        );

        if (measurements.length === 0) {
          return;
        }

        const postprocessing = await apiSequences.getSequencePostprocessing(this.sequence.id);

        const measurementPostprocessings = measurements.flatMap((measurement) => [
          {
            name: 'PDET',
            measurement_id: measurement.id,
            algorithm_pdet: postprocessing.algorithm_pdet,
            params: postprocessing.params_pdet,
          },
          {
            name: 'PEAK',
            measurement_id: measurement.id,
            algorithm_peak: postprocessing.algorithm_peak,
            params: postprocessing.params_peak,
          },
          {
            name: 'BASE',
            measurement_id: measurement.id,
            algorithm_base: postprocessing.algorithm_base,
            params: postprocessing.params_base,
          },
          {
            name: 'BINJ',
            measurement_id: measurement.id,
            algorithm_binj: postprocessing.algorithm_binj,
            params: postprocessing.params_binj,
          },
          {
            name: 'NOIS',
            measurement_id: measurement.id,
            algorithm_nois: postprocessing.algorithm_nois,
            params: postprocessing.params_nois,
          },
          {
            name: 'DTIM',
            measurement_id: measurement.id,
            params: postprocessing.params_dtim || {},
          },
        ]);

        console.warn('Measurements to restart:', measurements);
        console.warn('Postprocessing to apply:', postprocessing);
        await apiSequences.restartSequenceAnalysis(
          this.sequence.id,
          measurements.map((m) => m.id),
          measurementPostprocessings,
        );

        const onSequence = (s) => {
          this.sequence = s;
          this.pageTitle = `${s?.name}: Sequence HPLC.cloud`;
        };

        const data = await this.sequenceSocket.getFull();
        onSequence(data.sequence);
      },

      async handleSequencePostprocessingUpdated(postprocessing) {
        if (!postprocessing?.uuid) {
          await this.restartAnalysis();
          return;
        }

        await this.sequenceSocket.update({
          id: this.sequence.id,
          postprocessing_id: postprocessing.uuid,
        });

        await this.restartAnalysis();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .sequence-page {
    &__layout {
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    &__content-with-nav {
      display: flex;
      gap: 0;
      align-items: flex-start;
      min-height: 400px;
    }

    // Mobile horizontal menu
    &__nav-mobile {
      display: flex;
      flex-direction: row;
      gap: 6px;
      padding: 12px 32px;
      overflow-x: auto;
      border-bottom: 1px solid $color-bg-third;
      justify-content: center;
      position: sticky;
      top: 0;
      z-index: 5;
      background-color: white;

      @media (max-width: 420px) {
        padding: 10px;
        gap: 4px;
      }

      @screen sm {
        display: none;
      }
    }

    &__nav-mobile-item {
      padding: 8px 16px;
      border: none;
      border-radius: 6px;
      background-color: transparent;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      color: $color-text-third;
      transition: background-color 0.2s ease, color 0.2s ease;
      font: inherit;
      white-space: nowrap;

      i {
        font-size: 20px;
      }

      span {
        font-size: 13px;
        font-weight: 500;
      }

      // Small screens (up to 575px)
      @media (max-width: $screen-xs-max) {
        padding: 6px 14px;
        gap: 5px;

        i {
          font-size: 18px;
        }

        span {
          font-size: 12px;
        }
      }

      // Extra small screens (up to 440px)
      @media (max-width: $screen-xxs-max) {
        padding: 8px 6px;
        gap: 0;

        i {
          font-size: 16px;
        }

        span {
          font-size: 11px;
        }
      }

      &:hover {
        background-color: $color-bg-second;
        color: $color-text-primary;
      }

      &--active {
        background-color: $color-bg-second;
        color: $color-text-primary;
      }
    }

    // Desktop vertical menu
    &__nav-desktop {
      display: none;

      @screen sm {
        display: flex;
        flex-direction: column;
        gap: 0;
        width: 56px;
        align-items: center;
        position: sticky;
        top: 0;
        align-self: flex-start;
      }
    }

    &__nav-desktop-item {
      width: 56px;
      height: 56px;
      border: none;
      border-radius: 0 5px 5px 0;
      background-color: transparent;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: $color-text-third;
      transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
      font: inherit;

      i {
        font-size: 20px;
      }

      span {
        font-size: 10px;
        margin-top: 2px;
      }

      &:hover {
        background-color: $color-bg-second;
        color: $color-text-primary;
        box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
      }

      &--active {
        background-color: $color-bg-second;
        color: $color-text-primary;
        box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
      }
    }

    &__content {
      flex: 1;
      overflow-x: auto;
    }

    &__results {
      padding: 2px;
    }

    &__cell {
      &--sortable {
        cursor: pointer;
        transition: color 0.3s;
        padding-right: 0;

        &:hover {
          color: $color-text-default--active;
        }
      }

      &--selected {
        position: relative;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          height: 100%;
          width: 2px;
          background-color: $color-bg-primary;
        }
      }
    }

    &__sort-direction {
      transform: translateY(2px);
      opacity: 0;

      &--visible {
        transition: opacity 0.3s;
        opacity: 1;
      }
    }

    &__icon-add-sample {
      margin-right: 12px;
    }

    tfoot {
      tr {
        color: $color-text-primary;
        font-weight: $weight-bold;
        cursor: pointer;
        transition: background-color 0.3s;

        &:hover,
        &:focus-within {
          background-color: $color-bg-third;

          .sequence-page__label-add-sample {
            text-decoration: underline;
          }
        }

        &:active {
          color: $color-text-primary--active;
        }
      }
    }

    &__form-create-sample {
      display: flex;
      align-items: center;
      padding: 5px 0;
    }

    &__input-name {
      background-color: transparent;
      text-indent: 0;
    }

    &__wrapper-concentration {
      display: flex;
      align-items: end;
    }

    &__input-standard {
      flex: 1;
    }

    &__input-unit {
      max-width: 60px;
    }

    &__warning-not-editable {
      margin-top: 10px;
      color: $color-text-warning;
    }

    &__btn-add-sample,
    &__btn-hide-form {
      flex: none;
      min-width: 32px;
      margin-left: 5px;
    }

    &__wrapper-btn {
      display: inline-block;
    }

    &__btn-new-method {
      margin-left: 10px;
    }

    &__wrapper-pager {
      position: relative;
      display: flex;

      @screen sm {
        display: none;
      }
    }

    &__btn-back-desktop {
      display: none;

      @screen sm {
        display: block;
      }
    }

    &__btn-back-adaptive {
      height: 80px;

      @screen sm {
        display: none;
      }
    }

    &__id {
      color: $color-text-third;
    }

    &__favorite {
      margin-left: 10px;
    }

    &__calibration-mode {
      margin-top: 5px;
    }

    &__calibration-mode-label {
      color: $color-bg-warning;
    }

    &__sample {
      background-color: #f8f8f8;
      min-height: 340px;
    }

    &__button-group {
      display: flex;
      flex-direction: row;
      gap: 4px;

      @media (max-width: $screen-sm-max) {
        flex-direction: column;
      }
    }

    &__button-column {
      display: flex;
      flex-direction: row;
      gap: 4px;

      @media (max-width: $screen-sm-max) {
        flex-direction: column;
      }
    }

    &__action_button {
      justify-content: start;
    }
  }
</style>

<style lang="scss">
  .sequence__samples-expanded {
    display: flex;
    min-height: 1024px;
    flex-direction: column;
    @screen sm {
      flex-direction: row;
    }
    > div > .samples__pager {
      height: 80px;

      @screen sm {
        display: none;
      }
    }
  }

  .samples__sample-tabs-container {
    display: none;
    @screen sm {
      display: block;
    }
    position: relative;
    width: 64px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .samples__sample-expanded-container {
    width: 100%;
    @screen sm {
      width: calc(100% - 64px);
    }
    z-index: 1;
  }

  .samples__sample-tabs-inner-container {
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;
    padding-bottom: 512px;
    width: 100%;
  }

  .samples__sample-tab-button {
    padding-left: 16px;
    font-size: 13px;
    font-weight: bold;
    height: 48px;
    text-align: left;
    line-height: 48px;
    color: #00000066;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;

    &:hover {
      color: #000000;
      background-color: #00000011;
    }

    &--gray {
      color: #989898;
      text-decoration: line-through;
      background-color: #00000011;
    }

    &--selected {
      color: $color-text-primary--hover;
      background-color: #ffffff;
    }

    &--selected:hover {
      color: $color-text-primary--hover;
      background-color: #ffffff;
    }
  }
</style>
